import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { Shell } from "../components/Shell";
import { Ornament } from "../components/Ornament";
import type {
  GenerateShoppingListResponse,
  RecipePublic,
  RecipeShoppingItemPublic,
  ShoppingItemCreate,
  ShoppingItemUpdate,
  ShoppingListView,
} from "../lib/types";

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export function RecipeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery<RecipePublic>({
    queryKey: ["recipe", id],
    queryFn: () => api.get<RecipePublic>(`/api/recipes/${id}`),
    enabled: Boolean(id),
  });

  const del = useMutation({
    mutationFn: () => api.delete<void>(`/api/recipes/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recipes"] });
      nav("/", { replace: true });
    },
  });

  if (isLoading) {
    return (
      <Shell>
        <p className="label animate-fade-in text-ink-muted">Reading…</p>
      </Shell>
    );
  }

  if (isError || !data) {
    return (
      <Shell>
        <p className="label text-sienna">This page is missing from the book.</p>
        <Link to="/" className="label ink-link mt-4 inline-block text-ink">
          Back to recipes
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <article className="mx-auto">
        <header className="mb-20 grid grid-cols-12 gap-x-8 gap-y-12 border-b border-ink/80 pb-14">
          <aside
            className="col-span-12 md:col-span-3 animate-fade-up"
            style={{ animationDelay: "60ms" }}
          >
            <div className="label mb-2 text-ink-walnut">Entry</div>
            <div className="font-display text-3xl font-light text-sienna">
              N° {String(data.id).padStart(3, "0")}
            </div>

            <div className="mt-10 label text-ink-walnut">Serves</div>
            <div className="mt-1 font-display text-5xl font-light leading-none text-ink">
              {data.servings}
            </div>

            {data.source_url && (
              <>
                <div className="mt-10 label text-ink-walnut">Source</div>
                <a
                  href={data.source_url}
                  target="_blank"
                  rel="noreferrer"
                  className="label ink-link mt-1 block text-ink"
                >
                  {hostnameOf(data.source_url)}
                </a>
              </>
            )}
          </aside>

          <div
            className="col-span-12 md:col-span-9 animate-fade-up"
            style={{ animationDelay: "140ms" }}
          >
            <h1 className="display-1">{data.title}</h1>
            {data.notes && (
              <p className="mt-8 max-w-2xl border-l-2 border-sienna/60 pl-6 font-body text-xl italic leading-relaxed text-ink/85">
                {data.notes}
              </p>
            )}
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to={`/recipes/${data.id}/edit`} className="btn-ghost">
                Edit
              </Link>
              <button
                type="button"
                onClick={() => {
                  if (
                    confirm(
                      "Tear this page out of the book? This cannot be undone.",
                    )
                  ) {
                    del.mutate();
                  }
                }}
                className="btn-danger"
              >
                Remove
              </button>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-12 gap-x-8 gap-y-20">
          <section
            className="col-span-12 md:col-span-4 animate-fade-up"
            style={{ animationDelay: "260ms" }}
          >
            <div className="mb-8 flex items-center gap-4">
              <div className="label text-sienna">N° 01</div>
              <div className="label text-ink-walnut">Ingredients</div>
              <span className="hairline-ink flex-1 origin-left animate-rule-sweep" />
            </div>
            <ul>
              {data.ingredients.map((i, idx) => (
                <li
                  key={i.id}
                  className="grid grid-cols-[auto_1fr] items-baseline gap-4 border-b border-ink/10 py-4"
                >
                  <span className="label text-ink-muted">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="font-body text-lg leading-snug text-ink">
                    {i.raw_text}
                  </span>
                </li>
              ))}
              {data.ingredients.length === 0 && (
                <li className="font-body italic text-ink-muted">
                  No ingredients listed.
                </li>
              )}
            </ul>
          </section>

          <section
            className="col-span-12 md:col-span-8 animate-fade-up"
            style={{ animationDelay: "340ms" }}
          >
            <div className="mb-8 flex items-center gap-4">
              <div className="label text-sienna">N° 02</div>
              <div className="label text-ink-walnut">Method</div>
              <span className="hairline-ink flex-1 origin-left animate-rule-sweep" />
            </div>
            <ol className="space-y-12">
              {data.steps.map((s, idx) => (
                <li
                  key={s.id}
                  className="grid grid-cols-[auto_1fr] items-start gap-x-8"
                >
                  <div className="font-display text-6xl font-light leading-[0.85] text-sienna">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <p className="font-body text-xl leading-relaxed text-ink">
                    {s.text}
                  </p>
                </li>
              ))}
              {data.steps.length === 0 && (
                <li className="font-body italic text-ink-muted">
                  No method recorded.
                </li>
              )}
            </ol>
          </section>
        </div>

        <ShoppingListSection recipeId={data.id} />

        <div className="mt-28 flex items-center gap-6">
          <span className="hairline flex-1" />
          <Ornament className="h-4 w-20 text-sienna" />
          <span className="hairline flex-1" />
        </div>
      </article>
    </Shell>
  );
}


// ----- Shopping list section (Phase 13) -----

function ShoppingListSection({ recipeId }: { recipeId: number }) {
  const qc = useQueryClient();

  const list = useQuery<ShoppingListView>({
    queryKey: ["shopping-list", recipeId],
    queryFn: () =>
      api.get<ShoppingListView>(
        `/api/recipes/${recipeId}/shopping-list`,
      ),
  });

  const generate = useMutation<GenerateShoppingListResponse, Error, void>({
    mutationFn: () =>
      api.post<GenerateShoppingListResponse>(
        `/api/recipes/${recipeId}/shopping-list/generate`,
      ),
    onSuccess: (data) => {
      qc.setQueryData<ShoppingListView>(
        ["shopping-list", recipeId],
        {
          recipe_id: data.recipe_id,
          items: data.items,
          latest_generation_at: data.latest_generation_at,
          ingredients_changed_after_generation:
            data.ingredients_changed_after_generation,
        },
      );
    },
  });

  const view = list.data;
  const items = view?.items ?? [];
  const isEmpty = !list.isLoading && items.length === 0;
  const running = generate.isPending;
  const matched = items.filter((i) => i.product_id !== null).length;
  const unmatched = items.length - matched;

  return (
    <section
      className="col-span-12 mt-20 animate-fade-up border-t border-ink/80 pt-14"
      style={{ animationDelay: "420ms" }}
    >
      <div className="mb-8 flex items-center gap-4">
        <div className="label text-sienna">N° 03</div>
        <div className="label text-ink-walnut">Shopping list</div>
        <span className="hairline-ink flex-1 origin-left animate-rule-sweep" />
      </div>

      {list.isLoading && (
        <p className="label text-ink-muted">Reading…</p>
      )}

      {isEmpty && !running && (
        <div>
          <p className="max-w-2xl font-body text-lg italic leading-relaxed text-ink-walnut">
            No shopping list yet. Generating one matches every ingredient to
            a real Morrisons product and remembers your picks for future
            weeks. You can swap, remove, or add items afterwards.
          </p>
          <button
            type="button"
            onClick={() => generate.mutate()}
            className="btn-ink mt-6"
          >
            Generate shopping list
          </button>
          {generate.isError && (
            <p className="mt-4 font-body text-sm italic text-sienna">
              {generate.error instanceof ApiError
                ? generate.error.detail
                : String(generate.error)}
            </p>
          )}
        </div>
      )}

      {running && (
        <p className="font-body text-lg italic leading-relaxed text-ink-walnut">
          Reading the ingredients, searching Morrisons, picking products —
          this can take up to a minute.
        </p>
      )}

      {!running && items.length > 0 && view && (
        <>
          <ShoppingListSummary matched={matched} unmatched={unmatched} />
          {view.ingredients_changed_after_generation && (
            <div className="mb-6 border-l-2 border-sienna pl-4 font-body text-sm italic text-sienna">
              The recipe has changed since this list was generated.
              Regenerate to refresh the auto-picked items — your
              hand-added items will be preserved.
            </div>
          )}
          <ol>
            {items.map((item, idx) => (
              <ShoppingListRow
                key={item.id}
                item={item}
                position={idx + 1}
                recipeId={recipeId}
              />
            ))}
          </ol>
          <div className="mt-10 flex flex-wrap gap-6">
            <button
              type="button"
              onClick={() => {
                if (
                  confirm(
                    "Regenerate the shopping list? Your auto-generated picks will be replaced; any custom items you added by hand are kept.",
                  )
                ) {
                  generate.mutate();
                }
              }}
              className="btn-ghost"
            >
              Regenerate
            </button>
            <AddCustomItemForm recipeId={recipeId} />
          </div>
          {generate.isError && (
            <p className="mt-4 font-body text-sm italic text-sienna">
              {generate.error instanceof ApiError
                ? generate.error.detail
                : String(generate.error)}
            </p>
          )}
        </>
      )}
    </section>
  );
}


function ShoppingListSummary({
  matched,
  unmatched,
}: {
  matched: number;
  unmatched: number;
}) {
  const total = matched + unmatched;
  return (
    <p className="mb-6 max-w-2xl font-body text-lg italic leading-snug text-ink-walnut">
      The matcher found products for{" "}
      <span className="font-display not-italic text-xl text-ink">
        {matched}
      </span>{" "}
      of{" "}
      <span className="font-display not-italic text-xl text-ink">{total}</span>{" "}
      line{total === 1 ? "" : "s"}
      {unmatched > 0 && (
        <>
          {" "}—{" "}
          <span className="font-display not-italic text-xl text-sienna">
            {unmatched}
          </span>{" "}
          need{unmatched === 1 ? "s" : ""} a manual pick
        </>
      )}
      .
    </p>
  );
}


function formatPackSize(
  value: number | null,
  unit: string | null,
): string {
  if (value === null || !unit) return "";
  if (Number.isInteger(value)) return `${value}${unit}`;
  return `${value.toFixed(0)}${unit}`;
}


function formatNeededQty(qty: number, unit: string): string {
  const clean = Number.isInteger(qty) ? qty.toString() : qty.toFixed(1);
  const noSpace = ["g", "kg", "mg", "ml", "l", "cl"].includes(unit.toLowerCase());
  return noSpace ? `${clean}${unit}` : `${clean} ${unit}`;
}


// ----- One row in the shopping list table with swap/revise/remove -----

function ShoppingListRow({
  item,
  position,
  recipeId,
}: {
  item: RecipeShoppingItemPublic;
  position: number;
  recipeId: number;
}) {
  const qc = useQueryClient();
  const [showDrawer, setShowDrawer] = useState(false);
  const [guidance, setGuidance] = useState("");
  const [reviseError, setReviseError] = useState<string | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["shopping-list", recipeId] });

  const update = useMutation<RecipeShoppingItemPublic, Error, ShoppingItemUpdate>({
    mutationFn: (payload) =>
      api.patch<RecipeShoppingItemPublic>(
        `/api/recipes/${recipeId}/shopping-list/items/${item.id}`,
        payload,
      ),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: () =>
      api.delete<void>(
        `/api/recipes/${recipeId}/shopping-list/items/${item.id}`,
      ),
    onSuccess: invalidate,
  });

  const revise = useMutation<RecipeShoppingItemPublic, Error, string>({
    mutationFn: (text) =>
      api.post<RecipeShoppingItemPublic>(
        `/api/recipes/${recipeId}/shopping-list/items/${item.id}/revise`,
        { user_guidance: text },
      ),
    onSuccess: () => {
      invalidate();
      setGuidance("");
      setReviseError(null);
      setShowDrawer(false);
    },
    onError: (err) => {
      setReviseError(err instanceof ApiError ? err.detail : String(err));
    },
  });

  const isUnmatched = item.product_id === null;

  return (
    <li className="group animate-fade-up">
      <div className="grid grid-cols-[auto_1fr_auto] items-baseline gap-x-5 border-b border-ink/10 py-5">
        <div className="flex items-baseline gap-2 min-w-[3.5rem]">
          <span className="label text-ink-muted">
            {String(position).padStart(2, "0")}
          </span>
          {item.is_custom && (
            <span className="label text-sienna" title="added by hand">+</span>
          )}
        </div>
        <div>
          <div className="font-body text-base text-ink-walnut">
            {item.name}
            {item.quantity !== null && item.unit && (
              <span className="ml-3 font-mono text-xs text-ink-muted">
                needs {formatNeededQty(item.quantity, item.unit)}
              </span>
            )}
          </div>
          {isUnmatched ? (
            <div className="mt-2 font-body text-base italic text-sienna">
              — no product matched
              {item.notes && (
                <span className="ml-2 font-mono text-[11px] text-ink-muted">
                  ({item.notes})
                </span>
              )}
            </div>
          ) : (
            <div className="mt-2 font-display text-xl text-ink">
              {item.product_name ?? "—"}
              {item.product_price !== null && (
                <span className="ml-3 font-mono text-sm text-ink-walnut">
                  £{item.product_price.toFixed(2)}
                </span>
              )}
              {item.pack_size_value !== null && item.pack_size_unit && (
                <span className="ml-2 font-mono text-xs text-ink-muted">
                  {formatPackSize(item.pack_size_value, item.pack_size_unit)}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            type="button"
            onClick={() => setShowDrawer((s) => !s)}
            className="font-mono text-[10px] uppercase tracking-widest text-ink-walnut transition-colors hover:text-sienna"
          >
            <span className="ink-link">
              {showDrawer ? "close" : "ask matcher"}
            </span>{" "}
            {showDrawer ? "↑" : "↓"}
          </button>
          <button
            type="button"
            onClick={() => {
              if (confirm(`Remove "${item.name}" from the shopping list?`)) {
                remove.mutate();
              }
            }}
            disabled={remove.isPending}
            className="font-mono text-[10px] uppercase tracking-widest text-ink-muted transition-colors hover:text-sienna disabled:opacity-30"
          >
            × remove
          </button>
        </div>
      </div>

      {showDrawer && (
        <div className="mb-4 animate-fade-in">
          <div className="grid grid-cols-[auto_1fr_auto] gap-x-5">
            <span aria-hidden="true" />
            <div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const text = guidance.trim();
                  if (!text) return;
                  setReviseError(null);
                  revise.mutate(text);
                }}
                className="py-4"
              >
                <label
                  htmlFor={`guidance-${item.id}`}
                  className="label mb-2 block italic text-sienna"
                >
                  ask the matcher for something different —
                </label>
                <div className="flex items-center gap-3">
                  <input
                    id={`guidance-${item.id}`}
                    type="text"
                    value={guidance}
                    onChange={(e) => setGuidance(e.target.value)}
                    placeholder="cheaper · premium · different brand · bigger pack…"
                    disabled={revise.isPending}
                    className="flex-1 border-0 border-b border-ink/30 bg-transparent py-2 font-body text-sm italic text-ink focus:border-ink focus:outline-none disabled:opacity-40"
                  />
                  <button
                    type="submit"
                    disabled={revise.isPending || !guidance.trim()}
                    className="font-mono text-[10px] uppercase tracking-widest text-ink-walnut transition-colors hover:text-sienna disabled:opacity-30"
                  >
                    {revise.isPending ? "asking…" : "ask ↵"}
                  </button>
                </div>
                {reviseError && (
                  <div className="mt-2 font-body text-sm italic text-sienna">
                    {reviseError}
                  </div>
                )}
              </form>
              <QuantityEditor item={item} update={(v) => update.mutate(v)} />
            </div>
            <span aria-hidden="true" />
          </div>
        </div>
      )}
    </li>
  );
}


function QuantityEditor({
  item,
  update,
}: {
  item: RecipeShoppingItemPublic;
  update: (v: ShoppingItemUpdate) => void;
}) {
  const [qty, setQty] = useState<string>(
    item.quantity !== null ? String(item.quantity) : "",
  );
  const [unit, setUnit] = useState<string>(item.unit ?? "");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const parsed = qty.trim() === "" ? null : Number(qty);
        update({
          quantity: parsed !== null && !Number.isNaN(parsed) ? parsed : null,
          unit: unit.trim() === "" ? null : unit.trim(),
        });
      }}
      className="mt-2 flex flex-wrap items-baseline gap-3 border-t border-ink/10 pt-4"
    >
      <label className="label text-ink-muted">Needed quantity</label>
      <input
        type="number"
        step="any"
        value={qty}
        onChange={(e) => setQty(e.target.value)}
        className="w-20 border-0 border-b border-ink/30 bg-transparent py-1 text-right font-mono text-sm text-ink focus:border-ink focus:outline-none"
        placeholder="—"
      />
      <input
        type="text"
        value={unit}
        onChange={(e) => setUnit(e.target.value)}
        className="w-16 border-0 border-b border-ink/30 bg-transparent py-1 font-mono text-sm text-ink focus:border-ink focus:outline-none"
        placeholder="g / ml / …"
      />
      <button
        type="submit"
        className="font-mono text-[10px] uppercase tracking-widest text-ink-walnut transition-colors hover:text-sienna"
      >
        save
      </button>
    </form>
  );
}


// ----- Add custom item form -----

function AddCustomItemForm({ recipeId }: { recipeId: number }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const add = useMutation<
    RecipeShoppingItemPublic,
    Error,
    ShoppingItemCreate
  >({
    mutationFn: (payload) =>
      api.post<RecipeShoppingItemPublic>(
        `/api/recipes/${recipeId}/shopping-list/items`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shopping-list", recipeId] });
      setName("");
      setFormError(null);
      setOpen(false);
    },
    onError: (err) => {
      setFormError(err instanceof ApiError ? err.detail : String(err));
    },
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-ghost"
      >
        + Add custom item
      </button>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) {
          setFormError("Name is required.");
          return;
        }
        add.mutate({ name: name.trim() });
      }}
      className="flex w-full max-w-2xl flex-col gap-3 border border-ink/30 bg-parchment-deep/30 p-5"
    >
      <div className="label text-sienna">Custom shopping item</div>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="what it is (e.g. heck sausages)"
        className="border-0 border-b border-ink/30 bg-transparent py-2 font-body italic text-ink focus:border-ink focus:outline-none"
      />
      <div className="font-body text-xs italic text-ink-muted">
        Lands as unmatched — use <span className="font-mono">ask matcher</span> to
        find a Morrisons product.
      </div>
      {formError && (
        <div className="font-body text-sm italic text-sienna">{formError}</div>
      )}
      <div className="flex gap-4">
        <button
          type="submit"
          disabled={add.isPending}
          className="btn-ink"
        >
          {add.isPending ? "adding…" : "add"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="btn-ghost"
        >
          cancel
        </button>
      </div>
    </form>
  );
}
